import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Building2, Plus, Edit2, Trash2, Layers } from 'lucide-react';
import tenantsService from '../services/tenants';
import appsService from '../services/apps';
import usersService from '../services/users';
import { useApps } from '../contexts/AppsContext';
import { resolveDefaultAppId, setSelectedAppId } from '../utils/storage';
import PageHeader from '../components/common/PageHeader';
import DataCard from '../components/common/DataCard';
import SkeletonList from '../components/common/SkeletonList';
import ErrorState from '../components/common/ErrorState';
import Modal from '../components/common/Modal';
import { Typeahead } from '../components/ui/Typeahead';
import { NewUserForm } from '../components/NewUserForm';

const MFA_CHANNEL_OPTIONS = [
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' },
    { value: 'whatsapp', label: 'WhatsApp' },
];

/**
 * Structured per-tenant overrides (OTP channels, active-user limit, SSO). Controlled off the
 * tenant_config JSON string — every change re-stringifies the whole config so the raw JSON
 * textarea below stays in sync and unrelated keys are preserved. A key that is left at "inherit"
 * is deleted from tenant_config so the app-level value applies (config-scope principle).
 */
const TenantOverridesEditor = ({ tenantConfigStr, onChange, appId, tenantId }) => {
    const [seatUsage, setSeatUsage] = React.useState(null);
    React.useEffect(() => {
        let active = true;
        if (appId && tenantId) {
            appsService
                .getSeatUsage(appId, tenantId)
                .then((u) => {
                    if (active) setSeatUsage(u);
                })
                .catch(() => {
                    /* non-fatal */
                });
        }
        return () => {
            active = false;
        };
    }, [appId, tenantId]);

    let config = {};
    let parseFailed = false;
    try {
        config = tenantConfigStr ? JSON.parse(tenantConfigStr) : {};
    } catch (e) {
        parseFailed = true;
    }
    // Invalid JSON in the raw textarea — hide the structured editor and let the textarea drive.
    if (parseFailed || config === null || typeof config !== 'object') return null;

    const commit = (next) => onChange(JSON.stringify(next, null, 2));
    const overrideChannels = Array.isArray(config.mfa_channels);

    const toggleOverrideChannels = () => {
        const next = { ...config };
        if (overrideChannels) {
            delete next.mfa_channels;
            delete next.mfa_default_channel;
        } else {
            next.mfa_channels = ['email'];
        }
        commit(next);
    };

    const toggleChannel = (value) => {
        const current = Array.isArray(config.mfa_channels) ? config.mfa_channels : [];
        const arr = current.includes(value) ? current.filter((c) => c !== value) : [...current, value];
        const next = { ...config, mfa_channels: arr };
        if (next.mfa_default_channel && !arr.includes(next.mfa_default_channel)) {
            delete next.mfa_default_channel;
        }
        commit(next);
    };

    const setDefaultChannel = (value) => commit({ ...config, mfa_default_channel: value });

    const setLimit = (value) => {
        const next = { ...config };
        if (value === '') {
            delete next.max_active_users;
        } else {
            next.max_active_users = Number(value);
        }
        commit(next);
    };

    const ssoState = Object.prototype.hasOwnProperty.call(config, 'sso_enabled')
        ? (config.sso_enabled ? 'on' : 'off')
        : 'inherit';
    const setSso = (state) => {
        const next = { ...config };
        if (state === 'inherit') {
            delete next.sso_enabled;
        } else {
            next.sso_enabled = state === 'on';
        }
        commit(next);
    };

    return (
        <div className="space-y-4 rounded-xl border border-slate-100 p-4">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-700">Tenant Overrides</h4>
                {seatUsage && (
                    <span className="text-xs text-slate-500">
                        Seats{' '}
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium">
                            {seatUsage.used} / {seatUsage.limit === null ? '∞' : seatUsage.limit}
                        </span>
                    </span>
                )}
            </div>

            {/* OTP channels */}
            <div>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                    <input type="checkbox" checked={overrideChannels} onChange={toggleOverrideChannels} />
                    Override OTP channels for this tenant
                </label>
                {overrideChannels && (
                    <div className="mt-2 pl-6">
                        <div className="flex flex-wrap gap-2 mb-2">
                            {MFA_CHANNEL_OPTIONS.map((opt) => {
                                const selected = (config.mfa_channels || []).includes(opt.value);
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => toggleChannel(opt.value)}
                                        className={`px-3 py-1 rounded-full text-sm border ${
                                            selected
                                                ? 'bg-indigo-600 text-white border-indigo-600'
                                                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                        {(config.mfa_channels || []).length > 1 && (
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">Default</span>
                                <select
                                    value={config.mfa_default_channel || (config.mfa_channels || [])[0] || 'email'}
                                    onChange={(e) => setDefaultChannel(e.target.value)}
                                    className="rounded border border-slate-200 px-2 py-1 text-sm"
                                >
                                    {(config.mfa_channels || []).map((c) => {
                                        const o = MFA_CHANNEL_OPTIONS.find((x) => x.value === c);
                                        return (
                                            <option key={c} value={c}>
                                                {o ? o.label : c}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Active user limit */}
            <div>
                <label className="block text-sm text-slate-600 mb-1">Active User Limit</label>
                <input
                    type="number"
                    min="0"
                    value={config.max_active_users === undefined ? '' : config.max_active_users}
                    onChange={(e) => setLimit(e.target.value)}
                    placeholder="Inherit from app"
                    className="w-40 rounded border border-slate-200 px-2 py-1 text-sm"
                />
                <p className="text-xs text-slate-400 mt-1">Blank = inherit the app-level limit.</p>
            </div>

            {/* SSO tri-state */}
            <div>
                <label className="block text-sm text-slate-600 mb-1">Enterprise SSO</label>
                <select
                    value={ssoState}
                    onChange={(e) => setSso(e.target.value)}
                    className="w-40 rounded border border-slate-200 px-2 py-1 text-sm"
                >
                    <option value="inherit">Inherit from app</option>
                    <option value="on">Enabled</option>
                    <option value="off">Disabled</option>
                </select>
            </div>
        </div>
    );
};

const TenantsPage = () => {
    const { apps, loading: loadingApps, error: appsError } = useApps();
    const [appId, setAppId] = useState('');
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchParams] = useSearchParams();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [formData, setFormData] = useState({
        tenant_name: '',
        tenant_config: '{}',
        admin_username: '',
    });
    const [createTenantStep, setCreateTenantStep] = useState(1);
    const [userSelectionMode, setUserSelectionMode] = useState('existing');
    const [allUsers, setAllUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [newUserFormData, setNewUserFormData] = useState({
        username: '',
        email_id: '',
        first_name: '',
        last_name: '',
        phone_no: '',
        gender: '',
    });

    useEffect(() => {
        if (apps && apps.length > 0) {
            const appIdFromQuery = searchParams.get('app_id');
            const defaultAppId = resolveDefaultAppId(apps, appIdFromQuery);
            setAppId(defaultAppId);
            loadTenants(defaultAppId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apps, searchParams]);

    useEffect(() => {
        const loadUsersForApp = async () => {
            setUsersLoading(true);
            try {
                const users = await usersService.getAllActiveUsersAcrossApps();
                setAllUsers(users || []);
            } catch {
                setAllUsers([]);
            } finally {
                setUsersLoading(false);
            }
        };

        if (showCreateModal && userSelectionMode === 'existing' && createTenantStep === 2) {
            loadUsersForApp();
        }
    }, [showCreateModal, userSelectionMode, createTenantStep]);

    const filteredUsers = useMemo(() => {
        if (!userSearchTerm) return allUsers;
        const search = userSearchTerm.toLowerCase();
        return allUsers.filter(
            (user) =>
                user.username?.toLowerCase().includes(search) ||
                user.email_id?.toLowerCase().includes(search) ||
                `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().includes(search)
        );
    }, [allUsers, userSearchTerm]);

    const userOptions = useMemo(() => {
        return filteredUsers.map((user) => ({
            value: user.username,
            label: user.username,
            subtitle: `${user.first_name || ''} ${user.last_name || ''} ${
                user.email_id ? `(${user.email_id})` : ''
            }`.trim(),
        }));
    }, [filteredUsers]);

    const loadTenants = async (targetAppId) => {
        if (!targetAppId || targetAppId.trim() === '') {
            return;
        }
        setLoading(true);
        setError('');
        try {
            const data = await tenantsService.getAll(targetAppId.trim());
            setTenants(data || []);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load tenants');
        } finally {
            setLoading(false);
        }
    };

    const handleAppChange = (e) => {
        const selectedAppId = e.target.value;
        setAppId(selectedAppId);
        setSelectedAppId(selectedAppId);
        if (selectedAppId) {
            loadTenants(selectedAppId);
        }
    };

    const handleCreateTenant = async (e) => {
        e.preventDefault();
        if (!appId) {
            setError('App ID is required');
            return;
        }
        setError('');
        try {
            if (userSelectionMode === 'existing' && !formData.admin_username) {
                setError('Please select an existing user as tenant admin');
                return;
            }

            if (userSelectionMode === 'new') {
                if (
                    !newUserFormData.username ||
                    !newUserFormData.email_id ||
                    !newUserFormData.first_name ||
                    !newUserFormData.last_name
                ) {
                    setError('Please fill in all required fields for the new admin user');
                    return;
                }
            }

            let adminUsername = '';
            if (userSelectionMode === 'existing') {
                adminUsername = formData.admin_username;
            } else if (userSelectionMode === 'new') {
                await usersService.createUser(appId, {
                    username: newUserFormData.username,
                    email_id: newUserFormData.email_id,
                    first_name: newUserFormData.first_name,
                    last_name: newUserFormData.last_name,
                    phone_no: newUserFormData.phone_no || undefined,
                    gender: newUserFormData.gender || undefined,
                });
                adminUsername = newUserFormData.username;
            }

            await tenantsService.create({
                tenant_name: formData.tenant_name,
                app_id: appId,
                tenant_config: formData.tenant_config ? JSON.parse(formData.tenant_config) : null,
                admin_username: adminUsername,
            });
            setSuccessMessage('Tenant created successfully!');
            setShowCreateModal(false);
            resetForm();
            loadTenants(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to create tenant');
        }
    };

    const handleUpdateTenant = async (e) => {
        e.preventDefault();
        if (!selectedTenant) return;
        setError('');
        try {
            await tenantsService.updateConfig(selectedTenant.tenant_id, JSON.parse(formData.tenant_config || '{}'));
            setSuccessMessage('Tenant configuration updated successfully!');
            setShowUpdateModal(false);
            setSelectedTenant(null);
            resetForm();
            loadTenants(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to update tenant');
        }
    };

    const handleDeleteTenant = async () => {
        if (!selectedTenant) return;
        setError('');
        try {
            await tenantsService.deleteTenant(selectedTenant.tenant_id);
            setSuccessMessage(`Tenant ${selectedTenant.tenant_name} deleted successfully!`);
            setShowDeleteModal(false);
            setSelectedTenant(null);
            loadTenants(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to delete tenant');
        }
    };

    const handleToggleStatus = async (tenant, currentStatus) => {
        if (!window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this tenant?`))
            return;
        setError('');
        try {
            await tenantsService.updateStatus(tenant.tenant_id, currentStatus ? 0 : 1);
            setSuccessMessage(`Tenant ${currentStatus ? 'deactivated' : 'activated'} successfully!`);
            loadTenants(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to update tenant status');
        }
    };

    const resetForm = () => {
        setFormData({
            tenant_name: '',
            tenant_config: '{}',
            admin_username: '',
        });
        setUserSelectionMode('existing');
        setNewUserFormData({
            username: '',
            email_id: '',
            first_name: '',
            last_name: '',
            phone_no: '',
            gender: '',
        });
        setUserSearchTerm('');
        setCreateTenantStep(1);
    };

    const openUpdateModal = (tenant) => {
        setSelectedTenant(tenant);
        setFormData({
            tenant_config: JSON.stringify(tenant.tenant_config || {}, null, 2),
        });
        setShowUpdateModal(true);
    };

    const openDeleteModal = (tenant) => {
        setSelectedTenant(tenant);
        setShowDeleteModal(true);
    };

    return (
        <div className="space-y-4">
            <PageHeader
                title="Tenants"
                subtitle={`Manage tenants for ${appId ? `App: ${appId.substring(0, 8)}...` : 'selected app'}`}
                actions={
                    <div className="flex gap-2">
                        <select
                            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 min-w-[200px]"
                            value={appId}
                            onChange={handleAppChange}
                            disabled={loadingApps}
                        >
                            <option value="">Select an App</option>
                            {apps.map((app) => (
                                <option key={app.app_id} value={app.app_id}>
                                    {app.app_name} {app.is_active ? '' : '(Disabled)'}
                                </option>
                            ))}
                        </select>
                        {appId && (
                            <button
                                onClick={() => {
                                    resetForm();
                                    setCreateTenantStep(1);
                                    setShowCreateModal(true);
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                            >
                                <Plus className="h-4 w-4" /> Add Tenant
                            </button>
                        )}
                    </div>
                }
            />

            {successMessage && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm">
                    {successMessage}
                </div>
            )}

            {loadingApps ? (
                <SkeletonList />
            ) : appsError ? (
                <ErrorState message={appsError} />
            ) : loading ? (
                <SkeletonList />
            ) : error ? (
                <ErrorState message={error} />
            ) : !appId ? (
                <div className="rounded-xl border border-slate-200 bg-white/90 backdrop-blur p-8 text-center">
                    <p className="text-slate-500">Select an App to view tenants.</p>
                </div>
            ) : tenants.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-white/90 backdrop-blur p-8 text-center">
                    <p className="text-slate-500">No tenants found for the selected app.</p>
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {tenants.map((tenant) => (
                        <DataCard
                            key={tenant.tenant_id}
                            title={tenant.tenant_name}
                            tag={tenant.is_active ? 'Active' : 'Inactive'}
                            icon={Building2}
                            meta={[
                                { label: 'Tenant ID', value: tenant.tenant_id, copyable: true },
                                { label: 'App ID', value: tenant.app_id, copyable: true },
                                {
                                    label: 'Config',
                                    value:
                                        typeof tenant.tenant_config === 'string'
                                            ? tenant.tenant_config
                                            : JSON.stringify(tenant.tenant_config || {}),
                                },
                            ]}
                            description={
                                typeof tenant.tenant_config === 'string'
                                    ? tenant.tenant_config
                                    : JSON.stringify(tenant.tenant_config || {})
                            }
                            actions={
                                <div className="flex flex-wrap gap-2 mt-3">
                                    <Link
                                        to={`/users?app_id=${encodeURIComponent(
                                            tenant.app_id
                                        )}&tenant_id=${encodeURIComponent(tenant.tenant_id)}`}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-primary-700 bg-primary-50 border border-primary-100 hover:bg-primary-100"
                                    >
                                        <Layers className="h-3.5 w-3.5" /> Users
                                    </Link>
                                    <button
                                        onClick={() => openUpdateModal(tenant)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-slate-700 bg-slate-100 border border-slate-200 hover:bg-slate-200"
                                    >
                                        <Edit2 className="h-3.5 w-3.5" /> Edit
                                    </button>
                                    <button
                                        onClick={() => handleToggleStatus(tenant, tenant.is_active)}
                                        className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium ${
                                            tenant.is_active
                                                ? 'text-amber-700 bg-amber-50 border border-amber-100 hover:bg-amber-100'
                                                : 'text-emerald-700 bg-emerald-50 border border-emerald-100 hover:bg-emerald-100'
                                        }`}
                                    >
                                        {tenant.is_active ? 'Deactivate' : 'Activate'}
                                    </button>
                                    <button
                                        onClick={() => openDeleteModal(tenant)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-red-700 bg-red-50 border border-red-100 hover:bg-red-100"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" /> Delete
                                    </button>
                                </div>
                            }
                        />
                    ))}
                </div>
            )}

            {/* Create Tenant Modal */}
            <Modal
                isOpen={showCreateModal}
                onClose={() => {
                    resetForm();
                    setShowCreateModal(false);
                }}
                title="Create New Tenant"
                size="lg"
            >
                {/* Step Indicator */}
                <div className="flex items-center justify-center mb-6">
                    <div className="flex items-center">
                        <div
                            className={`flex items-center justify-center w-8 h-8 rounded-full ${
                                createTenantStep >= 1 ? 'bg-primary-600 text-white' : 'bg-slate-200 text-slate-500'
                            }`}
                        >
                            1
                        </div>
                        <div className={`w-16 h-1 mx-2 ${createTenantStep >= 2 ? 'bg-primary-600' : 'bg-slate-200'}`} />
                        <div
                            className={`flex items-center justify-center w-8 h-8 rounded-full ${
                                createTenantStep >= 2 ? 'bg-primary-600 text-white' : 'bg-slate-200 text-slate-500'
                            }`}
                        >
                            2
                        </div>
                    </div>
                </div>
                <div className="text-center mb-6">
                    <p className="text-sm text-slate-600">
                        {createTenantStep === 1 ? 'Step 1: Tenant Details' : 'Step 2: Tenant Admin User'}
                    </p>
                </div>

                <form onSubmit={handleCreateTenant} className="flex flex-col min-h-[400px]">
                    <div className="flex-1 space-y-4">
                        {/* Step 1: Tenant Details */}
                        {createTenantStep === 1 && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Tenant Name *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.tenant_name}
                                        onChange={(e) => setFormData({ ...formData, tenant_name: e.target.value })}
                                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                        placeholder="Tenant A"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Tenant Config (JSON)
                                    </label>
                                    <textarea
                                        rows={4}
                                        value={formData.tenant_config}
                                        onChange={(e) => setFormData({ ...formData, tenant_config: e.target.value })}
                                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-200"
                                        placeholder='{"custom_field": "value"}'
                                    />
                                </div>

                                {error && (
                                    <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                        {error}
                                    </div>
                                )}
                            </>
                        )}

                        {/* Step 2: Tenant Admin User */}
                        {createTenantStep === 2 && (
                            <>
                                <div>
                                    <p className="text-sm font-medium text-slate-700 mb-3">Tenant Admin User</p>

                                    <div className="flex gap-4 mb-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="tenantAdminUserMode"
                                                value="existing"
                                                checked={userSelectionMode === 'existing'}
                                                onChange={(e) => {
                                                    setUserSelectionMode(e.target.value);
                                                    setFormData({ ...formData, admin_username: '' });
                                                    setNewUserFormData({
                                                        username: '',
                                                        email_id: '',
                                                        first_name: '',
                                                        last_name: '',
                                                        phone_no: '',
                                                        gender: '',
                                                    });
                                                }}
                                                className="text-primary-600 focus:ring-primary-500"
                                            />
                                            <span className="text-sm font-medium text-slate-700">Existing User</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="tenantAdminUserMode"
                                                value="new"
                                                checked={userSelectionMode === 'new'}
                                                onChange={(e) => {
                                                    setUserSelectionMode(e.target.value);
                                                    setFormData({ ...formData, admin_username: '' });
                                                }}
                                                className="text-primary-600 focus:ring-primary-500"
                                            />
                                            <span className="text-sm font-medium text-slate-700">New User</span>
                                        </label>
                                    </div>

                                    {userSelectionMode === 'existing' ? (
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                                Select Existing User <span className="text-red-500">*</span>
                                            </label>
                                            <Typeahead
                                                options={userOptions}
                                                value={formData.admin_username}
                                                onChange={(value) =>
                                                    setFormData({ ...formData, admin_username: value || '' })
                                                }
                                                onSearch={(term) => setUserSearchTerm(term)}
                                                placeholder="Search by username, email, or name..."
                                                displayKey="label"
                                                valueKey="value"
                                                loading={usersLoading}
                                            />
                                            {formData.admin_username && (
                                                <p className="text-xs text-slate-500 mt-1.5">
                                                    Selected: {formData.admin_username}
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <NewUserForm formData={newUserFormData} setFormData={setNewUserFormData} />
                                    )}
                                </div>

                                {error && (
                                    <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                        {error}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Footer buttons */}
                    <div className="flex gap-3 pt-4 mt-auto border-t border-slate-200">
                        {createTenantStep === 1 ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!formData.tenant_name.trim()) {
                                            setError('Tenant name is required');
                                            return;
                                        }
                                        if (formData.tenant_config) {
                                            try {
                                                JSON.parse(formData.tenant_config);
                                            } catch {
                                                setError('Tenant config must be valid JSON');
                                                return;
                                            }
                                        }
                                        setError('');
                                        setCreateTenantStep(2);
                                    }}
                                    className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                                >
                                    Next: Admin User
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        resetForm();
                                    }}
                                    className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setCreateTenantStep(1);
                                        setError('');
                                    }}
                                    className="px-4 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                                >
                                    Create Tenant
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        resetForm();
                                    }}
                                    className="px-4 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                            </>
                        )}
                    </div>
                </form>
            </Modal>

            {/* Update Tenant Modal */}
            <Modal
                isOpen={showUpdateModal}
                onClose={() => setShowUpdateModal(false)}
                title={`Update ${selectedTenant?.tenant_name || 'Tenant'}`}
                size="md"
            >
                <form onSubmit={handleUpdateTenant} className="space-y-4">
                    <TenantOverridesEditor
                        tenantConfigStr={formData.tenant_config}
                        onChange={(str) => setFormData({ ...formData, tenant_config: str })}
                        appId={appId}
                        tenantId={selectedTenant?.tenant_id}
                    />

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Tenant Config (JSON)</label>
                        <textarea
                            rows={6}
                            value={formData.tenant_config}
                            onChange={(e) => setFormData({ ...formData, tenant_config: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder='{"key": "value"}'
                        />
                    </div>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                        >
                            Update Tenant
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowUpdateModal(false)}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Delete Tenant Modal */}
            <Modal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedTenant(null);
                }}
                title="Delete Tenant"
                size="md"
            >
                <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                        Are you sure you want to delete the tenant <strong>{selectedTenant?.tenant_name}</strong>? This
                        action will permanently delete the tenant and all associated data including:
                    </p>
                    <ul className="text-sm text-slate-600 list-disc list-inside space-y-1 ml-2">
                        <li>All user mappings for this tenant</li>
                        <li>All user role mappings for this tenant</li>
                        <li>All authentication sessions for this tenant</li>
                    </ul>
                    <p className="text-sm font-medium text-red-600">This action cannot be undone.</p>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="button"
                            onClick={handleDeleteTenant}
                            className="flex-1 rounded-lg bg-red-600 text-white font-medium py-2.5 hover:bg-red-700"
                        >
                            Delete Tenant
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowDeleteModal(false);
                                setSelectedTenant(null);
                            }}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default TenantsPage;
